/**
 * Pasarle el presupuesto al cliente, apenas se carga el numero del tecnico.
 *
 * POR QUE ES UN PASO Y NO UN ITEM DE MENU
 * ---------------------------------------
 * El presupuesto estaba escrito (`mensajePresupuesto`) y se podia mandar, pero
 * habia que ir a buscarlo al menu "Mas" de la ficha. El momento real en que se
 * manda es OTRO: el tecnico contesta, se cargan los dos numeros, y lo que sigue
 * es decirselo al cliente. Si en ese momento la pantalla no lo ofrece, el
 * mensaje se escribe a mano en el celular —y ahi es donde uno dice "seña" y el
 * otro "anticipo", y donde el precio sale en una moneda que el texto no
 * declara. Por eso `Reparaciones` abre este dialogo solo al guardar los costos.
 *
 * SE VE ANTES DE MANDARSE, Y SE PUEDE TOCAR
 * ------------------------------------------
 * El texto viene armado y completo; el textarea esta para la linea que este
 * presupuesto puntual necesita ("el repuesto tarda tres dias"), no para
 * reescribirlo. La consistencia la da el texto precargado, no prohibir el
 * teclado.
 *
 * EL NUMERO SE MUESTRA ANTES DE ABRIR NADA
 * -----------------------------------------
 * Misma regla que el remito y que `BotonWhatsapp`: el telefono argentino con el
 * 15 es ambiguo y la heuristica puede errarle. Sin telefono cargado el dialogo
 * no desaparece —queda "Copiar" y el link abre el selector de contactos de
 * WhatsApp—, porque el mensaje sigue siendo util pegado en un chat que ya
 * existe.
 */
import { useEffect, useState } from "react";
import { Check, Copy, MessageCircle } from "lucide-react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { linkWhatsapp, normalizarTelefonoAr } from "@/lib/remito";

import { mensajePresupuesto } from "./mensajes";
import { leerPlata, nombreOrden, type OrdenTaller } from "./logica";

export interface DialogPresupuestoProps {
  /** `null` cierra el dialogo. */
  orden: OrdenTaller | null;
  /** El de la ficha del cliente. Sin el, queda copiar. */
  telefono?: string | null;
  /** Que porcentaje se pide para arrancar, de la configuracion. */
  anticipoPct?: number;
  onCerrar: () => void;
}

export function DialogPresupuesto({
  orden,
  telefono,
  anticipoPct = 60,
  onCerrar,
}: DialogPresupuestoProps): JSX.Element {
  const [texto, setTexto] = useState("");
  const [copiado, setCopiado] = useState(false);

  // El mensaje se rearma cada vez que se abre con otra orden o con otros
  // numeros: corregir el presupuesto tiene que traer el precio corregido, no el
  // texto que quedo de la vez anterior.
  useEffect(() => {
    setTexto(orden ? mensajePresupuesto(orden, anticipoPct) : "");
    setCopiado(false);
  }, [orden, anticipoPct]);

  const tel = normalizarTelefonoAr(telefono);
  const plata = orden ? leerPlata(orden) : null;

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin permiso de portapapeles no hay nada que avisar: el texto esta a la
      // vista y se selecciona a mano.
    }
  }

  return (
    <Dialog open={orden !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md" mobile="hoja">
        {orden && plata ? (
          <>
            <DialogHeader>
              <DialogTitle>Pasar el presupuesto</DialogTitle>
              <DialogDescription>
                {nombreOrden(orden)} · {orden.equipo}. Lo que se le manda al cliente por WhatsApp.
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <Card
                variant="muted"
                padding="compacto"
                className="flex items-baseline justify-between gap-4"
              >
                <span className="text-sm font-medium">Precio al cliente</span>
                <Importe
                  monto={plata.precio ?? 0}
                  moneda={plata.moneda}
                  tamano="lg"
                  className="font-semibold"
                />
              </Card>

              {tel ? (
                <Aviso
                  tono={tel.confianza === "alta" ? "neutral" : "warning"}
                  icono={MessageCircle}
                  compacto
                >
                  {tel.confianza === "alta"
                    ? `Se abre el chat con ${tel.legible}.`
                    : `Ojo: ${tel.legible} no tiene la forma de un celular argentino. Revisalo antes de mandar.`}
                </Aviso>
              ) : (
                <Aviso tono="warning" compacto>
                  Este cliente no tiene teléfono cargado. Copiá el mensaje y pegalo en el chat.
                </Aviso>
              )}

              <Campo
                label="El mensaje"
                ayuda="Viene escrito con el precio, lo que ya pagó y lo que falta para arrancar. Se puede agregar algo antes de mandarlo."
              >
                <Textarea
                  id="ot-presupuesto-mensaje"
                  rows={10}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                />
              </Campo>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onCerrar}>
                Ahora no
              </Button>
              <Button type="button" variant="outline" onClick={() => void copiar()}>
                {copiado ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                {copiado ? "Copiado" : "Copiar"}
              </Button>
              {/* Abre en otra pestaña y cierra el dialogo: el paso ya esta dado
                  y lo que queda es esperar la respuesta del cliente. */}
              <Button asChild type="button">
                <a
                  href={linkWhatsapp(tel?.e164 ?? "", texto)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onCerrar}
                >
                  <MessageCircle aria-hidden="true" />
                  Mandar por WhatsApp
                </a>
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
